/**
 * LO-02 city packs, eager and tiny (the manifest's gates, badge and map check): what this device
 * keeps about downloaded packs in the KV store. The pack files themselves live in IndexedDB
 * (store.ts, lazy chunk); this is the small index of them.
 */
import type { AppContext } from '../../game/context.js';
import { has, t } from '../../i18n/index.js';
import { currentStep } from '../../services/firstSession.js';

/** city → the pack version on this device, its name, size and level range */
export const INSTALLED_KEY = 'citypacks.installed';
/** { cellular, auto }: download over mobile data (off by default); download new cities automatically (on) */
export const PREFS_KEY = 'citypacks.prefs';
/** cities the server published, as of the last list (for the "new city" badge) */
export const KNOWN_KEY = 'citypacks.known';
/** cities the player has seen in the Cities screen */
export const SEEN_KEY = 'citypacks.seen';
/** cities the player deleted: never downloaded again without asking */
export const REMOVED_KEY = 'citypacks.removed';
/** city → version of a download that started and has not finished (resumed next session) */
export const PARTIAL_KEY = 'citypacks.partial';
/** cities whose unfinished download the player paused: not resumed on their own next session */
export const PAUSED_KEY = 'citypacks.paused';
/**
 * city → a newer version of an installed pack that could not download on its own (the network
 * may be mobile data, or is): the player decides in the Cities screen, and the rail says so.
 */
export const WAITING_KEY = 'citypacks.waiting';
/**
 * session.checked marks: 'citypacks' once the packs on this device are registered, NET_CHECK
 * once unfinished downloads resumed and the published list was asked for (downloads on).
 */
export const NET_CHECK = 'citypacks:net';

export interface Installed {
  city: string;
  version: number;
  name: string;
  bytes: number;
  first: number;
  last: number;
  installedAt: number;
}

export interface Prefs {
  cellular: boolean;
  auto: boolean;
}

type Ctx = Pick<AppContext, 'services'>;

export function readJson<T>(a: Ctx, key: string, fallback: T): T {
  try {
    const raw = a.services.kv.get(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

export function writeJson(a: Ctx, key: string, value: unknown): void {
  a.services.kv.set(key, JSON.stringify(value));
}

export function installedPacks(a: Ctx): Record<string, Installed> {
  const v = readJson<Record<string, Installed>>(a, INSTALLED_KEY, {});
  return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
}

export function prefsOf(a: Ctx): Prefs {
  const p = readJson<Partial<Prefs>>(a, PREFS_KEY, {});
  return { cellular: p.cellular === true, auto: p.auto !== false };
}

function strings(a: Ctx, key: string): string[] {
  const v = readJson<unknown>(a, key, []);
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

export const knownCities = (a: Ctx) => strings(a, KNOWN_KEY);
export const seenCities = (a: Ctx) => strings(a, SEEN_KEY);
export const removedCities = (a: Ctx) => strings(a, REMOVED_KEY);
export const pausedCities = (a: Ctx) => strings(a, PAUSED_KEY);

/** Published cities the player has neither downloaded nor looked at yet. */
export function newCityCount(a: Ctx): number {
  const inst = installedPacks(a);
  const seen = new Set(seenCities(a));
  return knownCities(a).filter((c) => !inst[c] && !seen.has(c)).length;
}

export function waitingUpdates(a: Ctx): Record<string, number> {
  const v = readJson<Record<string, number>>(a, WAITING_KEY, {});
  return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
}

/** Installed packs with an update that waits for the player (see WAITING_KEY). */
export function waitingUpdateCount(a: Ctx): number {
  const inst = installedPacks(a);
  return Object.entries(waitingUpdates(a)).filter(([c, v]) => !!inst[c] && typeof v === 'number' && v > inst[c].version).length;
}

/** Flow A (the first session) is over: no "new city" badge before the save prompt (as the vote's rail dot). */
export const firstSessionOver = (a: Ctx): boolean => currentStep(a.services.store.state) === 'done';

/** Packs are on this device: they register on every start, whatever the remote flag says. */
export const hasPacks = (a: Ctx): boolean => Object.keys(installedPacks(a)).length > 0;

/**
 * The `cityPacks` remote flag: the Cities rail entry, new downloads and updates. It does not
 * gate the packs already on this device (see manifest.ts).
 */
export const downloadsOn = (a: Pick<AppContext, 'remote'>): boolean => a.remote.flags.cityPacks !== false;

/**
 * A city's name: a translated one (this feature's, or the pack's own strings once the pack is
 * registered: `city.<id>.name`, the key the map reads), the pack's English name, else from its id
 * (`mexico_city` → Mexico City).
 */
export function cityLabel(id: string, packName?: string | null): string {
  const key = `feature.citypacks.city.${id}`;
  if (has(key)) return t(key);
  if (has(`city.${id}.name`)) return t(`city.${id}.name`);
  if (packName) return packName;
  return id
    .split('_')
    .filter(Boolean)
    .map((w) => w[0]!.toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Published cities not on this device (as of the last list), for the journey map's "download"
 * teasers; none while new downloads are off.
 */
export function downloadableCities(a: Pick<AppContext, 'services' | 'remote'>): { city: string; name: string }[] {
  if (!downloadsOn(a)) return [];
  const inst = installedPacks(a);
  return knownCities(a)
    .filter((c) => !inst[c])
    .map((city) => ({ city, name: cityLabel(city) }));
}
